import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Hash, User, MessageSquare, Loader2 } from 'lucide-react';
import { searchApi } from '@/api/search';
import { useMobile } from '@/pages/chat/ChatPage';
import NickName from '@/components/ui/NickName';

interface Props {
  onClose: () => void;
  initialQuery?: string;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function GlobalSearchModal({ onClose, initialQuery = '' }: Props) {
  const navigate = useNavigate();
  const { setShowSidebar } = useMobile();
  const [q, setQ] = useState(initialQuery);
  const debounced = useDebounced(q, 300);
  const enabled = debounced.trim().length >= 2;

  const { data: messages, isLoading: lm } = useQuery({
    queryKey: ['search', 'global', debounced],
    queryFn: () => searchApi.global(debounced, 20),
    enabled,
  });
  const { data: channels, isLoading: lc } = useQuery({
    queryKey: ['search', 'channels', debounced],
    queryFn: () => searchApi.channels(debounced, 10),
    enabled,
  });
  const { data: users, isLoading: lu } = useQuery({
    queryKey: ['search', 'users', debounced],
    queryFn: () => searchApi.users(debounced, 10),
    enabled,
  });

  const loading = enabled && (lm || lc || lu);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // На мобилке после навигации обязательно сворачиваем sidebar — иначе он
   // остаётся поверх контента и кажется, что «клик не сработал».
   const goChannel = (id: string) => { setShowSidebar(false); navigate(`/channel/${id}`); onClose(); };
   const goDM = (id: string) => { setShowSidebar(false); navigate(`/dm/${id}`); onClose(); };
   const goProfile = (username: string) => { setShowSidebar(false); navigate(`/u/${username}`); onClose(); };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 pt-[10vh]" onClick={onClose}>
      <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-xl shadow-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-surface-800">
          <Search size={16} className="text-surface-500" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по сообщениям, каналам, людям…"
            className="flex-1 bg-transparent outline-none text-surface-100 text-sm"
          />
          {loading && <Loader2 size={14} className="animate-spin text-surface-500" />}
          <button onClick={onClose} className="text-surface-500 hover:text-surface-200"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {!enabled && (
            <p className="text-xs text-surface-500 text-center py-6">Введите минимум 2 символа</p>
          )}

          {channels && channels.length > 0 && (
            <section>
              <h4 className="text-[10px] uppercase text-surface-600 mb-1">Каналы</h4>
              {channels.map((ch) => (
                <button key={ch.id} onClick={() => goChannel(ch.id)} className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-surface-800 text-sm text-surface-200">
                  <Hash size={13} className="text-surface-500" />
                  <span className="truncate">{ch.displayName}</span>
                  {ch.topic && <span className="text-xs text-surface-500 truncate">— {ch.topic}</span>}
                </button>
              ))}
            </section>
          )}

          {users && users.length > 0 && (
            <section>
              <h4 className="text-[10px] uppercase text-surface-600 mb-1">Пользователи</h4>
              {users.map((u) => (
                <button key={u.id} onClick={() => goProfile(u.username)} className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-surface-800 text-sm text-surface-200">
                  <User size={13} className="text-surface-500" />
                  <NickName user={u} className="truncate" />
                  <span className="text-xs text-surface-600 font-mono truncate">@{u.username}</span>
                </button>
              ))}
            </section>
          )}

          {messages && messages.messages.length > 0 && (
            <section>
              <h4 className="text-[10px] uppercase text-surface-600 mb-1">Сообщения в каналах</h4>
              {messages.messages.map((m) => (
                <button
                  key={m.id}
                  onClick={() => m.channelId && goChannel(m.channelId)}
                  className="flex items-start gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-surface-800"
                >
                  <MessageSquare size={13} className="text-surface-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-surface-200 truncate">{m.content}</p>
                    <p className="text-[10px] text-surface-600">{new Date(m.createdAt).toLocaleString('ru-RU')}</p>
                  </div>
                </button>
              ))}
            </section>
          )}

          {messages && messages.dms.length > 0 && (
            <section>
              <h4 className="text-[10px] uppercase text-surface-600 mb-1">Личные сообщения</h4>
              {messages.dms.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    const peer = m.senderId !== undefined && m.recipientId !== undefined
                      ? (m.recipientId as string)
                      : '';
                    if (peer) goDM(peer);
                  }}
                  className="flex items-start gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-surface-800"
                >
                  <MessageSquare size={13} className="text-surface-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-surface-200 truncate">{m.content}</p>
                    <p className="text-[10px] text-surface-600">{new Date(m.createdAt).toLocaleString('ru-RU')}</p>
                  </div>
                </button>
              ))}
            </section>
          )}

          {enabled && !loading
            && (!messages || (messages.messages.length === 0 && messages.dms.length === 0))
            && (!channels || channels.length === 0)
            && (!users || users.length === 0) && (
            <p className="text-xs text-surface-500 text-center py-6">Ничего не найдено</p>
          )}
        </div>
      </div>
    </div>
  );
}
